// 中央定義：LINE 官方訊息結構與預設值
// 以純 JS 物件為主，避免額外依賴。

export const LINE_MESSAGE_TYPES = {
  TEXT: "text",
  IMAGE: "image",
  TEMPLATE: "template",
  FLEX: "flex",
};

export const DEFAULT_ALT_TEXT = "LINE Message";

export const createTextMessage = (text = "Hello from Flex App") => ({
  type: LINE_MESSAGE_TYPES.TEXT,
  text,
});

export const createImageMessage = ({
  originalContentUrl = "",
  previewImageUrl = "",
} = {}) => ({
  type: LINE_MESSAGE_TYPES.IMAGE,
  originalContentUrl,
  previewImageUrl: previewImageUrl || originalContentUrl,
});

export const createTemplateMessage = ({
  altText = DEFAULT_ALT_TEXT,
  title = "Title",
  text = "Template text",
  thumbnailImageUrl = "",
  actions = [
    { type: "message", label: "OK", text: "OK" },
    { type: "uri", label: "More", uri: "https://example.com" },
  ],
} = {}) => ({
  type: LINE_MESSAGE_TYPES.TEMPLATE,
  altText,
  template: {
    type: "buttons",
    thumbnailImageUrl,
    title,
    text,
    actions,
  },
});

// Flex 基礎：單一 bubble 預設
export const createFlexBubble = ({
  heroUrl = "",
  title = "Title",
  titleColor = "#000000",
  titleSize = "xl",
  bodyText = "",
  bodyTextColor = "#111111",
  buttons = [],
  backgroundColor = "#ffffff",
} = {}) => {
  const contents = [
    {
      type: "text",
      text: title || " ",
      weight: "bold",
      size: titleSize,
      color: titleColor,
      wrap: true,
      margin: "md",
      align: "center",
    },
  ];

  if (bodyText) {
    contents.push({
      type: "text",
      text: bodyText,
      size: "sm",
      color: bodyTextColor,
      wrap: true,
      margin: "md",
    });
  }

  if (buttons?.length) {
    contents.push({ type: "separator", margin: "md" });
    contents.push({
      type: "box",
      layout: "vertical",
      spacing: "sm",
      contents: buttons,
    });
  }

  return {
    type: "bubble",
    ...(heroUrl
      ? {
          hero: {
            type: "image",
            url: heroUrl,
            size: "full",
            aspectRatio: "20:13",
            aspectMode: "cover",
          },
        }
      : {}),
    body: {
      type: "box",
      layout: "vertical",
      paddingAll: "16px",
      spacing: "md",
      backgroundColor,
      contents,
    },
  };
};

export const createFlexCarousel = (bubbles = [createFlexBubble()]) => ({
  type: "carousel",
  contents: bubbles,
});

export const createFlexMessage = ({
  altText = DEFAULT_ALT_TEXT,
  bubbles = [createFlexBubble()],
} = {}) => ({
  type: LINE_MESSAGE_TYPES.FLEX,
  altText,
  contents: bubbles.length > 1 ? createFlexCarousel(bubbles) : bubbles[0],
});

// 簡易驗證：僅做必要檢查避免送出錯誤格式
export const validators = {
  isHttpsUrl: (url) => {
    if (!url) return false;
    return /^https:\/\//i.test(url);
  },
  required: (value) => value !== undefined && value !== null && value !== "",
};

// 根據類型補齊缺失欄位，確保送出時格式完整
export const normalizeMessage = (msg) => {
  if (!msg) return null;
  if (msg.type === LINE_MESSAGE_TYPES.TEXT) {
    return { ...createTextMessage(), ...msg };
  }
  if (msg.type === LINE_MESSAGE_TYPES.IMAGE) {
    const base = createImageMessage({});
    return {
      ...base,
      ...msg,
      previewImageUrl: msg.previewImageUrl || msg.originalContentUrl || base.previewImageUrl,
    };
  }
  if (msg.type === LINE_MESSAGE_TYPES.TEMPLATE) {
    const base = createTemplateMessage({});
    return {
      ...base,
      ...msg,
      altText: msg.altText || base.altText,
      template: { ...base.template, ...(msg.template || {}) },
    };
  }
  if (msg.type === LINE_MESSAGE_TYPES.FLEX) {
    const base = createFlexMessage();
    const contents = msg.contents || base.contents;
    return {
      ...base,
      ...msg,
      altText: msg.altText || base.altText,
      contents,
    };
  }
  return msg;
};

// 預設一組多訊息範例，方便初始化
export const defaultMessageBundle = () => [
  createTextMessage("感謝使用 Flex App！"),
  createImageMessage({
    originalContentUrl: "https://placehold.co/800x600.png",
    previewImageUrl: "https://placehold.co/400x300.png",
  }),
  createTemplateMessage({}),
  createFlexMessage({}),
];
